import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Zap, Cpu, Shield, Rocket, Code, Database, Cloud, Lock } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const GetStarted = () => {
  const [scrollY, setScrollY] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
        buttonBg: 'bg-blue-600 hover:bg-blue-700',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
        buttonBg: 'bg-cyan-600 hover:bg-cyan-700',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
      buttonBg: 'bg-purple-600 hover:bg-purple-700',
    };
  };

  const classes = getThemeClasses();

  const steps = [
    {
      icon: <Code className="w-6 h-6" />,
      title: 'Create Account',
      description: 'Sign up with your email and set up your quantum workspace in minutes',
      time: '2 min',
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: 'Choose Your Plan',
      description: 'Select from our flexible plans tailored to your computational needs',
      time: '1 min',
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: 'Configure Environment',
      description: 'Set up your quantum computing environment with our guided setup',
      time: '5 min',
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: 'Start Building',
      description: 'Access our API, run quantum algorithms, and deploy your solutions',
      time: 'Instant',
    },
  ];

  const plans = [
    {
      name: 'Starter',
      price: 'Free',
      description: 'Perfect for learning and experimentation',
      features: [
        '10 quantum hours/month',
        'Basic quantum algorithms',
        'Community support',
        'Documentation access',
        'Single user',
      ],
      cta: 'Start Free',
      popular: false,
    },
    {
      name: 'Professional',
      price: '$299',
      period: '/month',
      description: 'For serious researchers and developers',
      features: [
        '100 quantum hours/month',
        'Advanced algorithms',
        'Priority support',
        'API access',
        'Up to 5 users',
        'Custom integrations',
      ],
      cta: 'Start Trial',
      popular: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'For organizations and institutions',
      features: [
        'Unlimited quantum hours',
        'Full algorithm library',
        'Dedicated support',
        'SLA guarantees',
        'Unlimited users',
        'On-premise deployment',
        'Custom development',
      ],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const features = [
    { icon: <Zap className="w-5 h-5" />, text: 'Lightning-fast setup' },
    { icon: <Shield className="w-5 h-5" />, text: 'Enterprise security' },
    { icon: <Cpu className="w-5 h-5" />, text: 'Quantum-ready infrastructure' },
    { icon: <Lock className="w-5 h-5" />, text: '256-bit encryption' },
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes slide-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slide-in-left {
          from { transform: translateX(-50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slide-in-right {
          from { transform: translateX(50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out forwards;
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.8s ease-out forwards;
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.8s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[60vh] flex items-center ${classes.bg} overflow-hidden`}>
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 ${theme === 'light' ? 'bg-blue-400' : theme === 'dark' ? 'bg-cyan-400' : 'bg-purple-400'} rounded-full opacity-30`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center">
          <div className="animate-slide-in-up">
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${classes.text} mb-6`}>
              Get Started with{' '}
              <span className={`bg-gradient-to-r ${classes.accent} bg-clip-text text-transparent`}>
                Quantum Computing
              </span>
            </h1>
            <p className={`text-lg sm:text-xl ${classes.subtext} max-w-3xl mx-auto mb-8`}>
              Begin your quantum journey in minutes. Access cutting-edge quantum computing resources,
              advanced algorithms, and comprehensive documentation.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 ${classes.cardBg} px-4 py-2 rounded-full border ${classes.cardBorder}`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <span className={classes.accentText}>{feature.icon}</span>
                  <span className={`text-sm ${classes.text}`}>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Four Simple Steps
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Get up and running with quantum computing in less than 10 minutes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 border ${classes.cardBorder} animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative mb-6">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-4`}>
                    {step.icon}
                  </div>
                  <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-r ${classes.accent} text-white flex items-center justify-center text-sm font-bold`}>
                    {index + 1}
                  </div>
                </div>
                <h3 className={`text-xl font-bold ${classes.text} mb-2`}>{step.title}</h3>
                <p className={`${classes.subtext} mb-4 text-sm`}>{step.description}</p>
                <div className={`inline-flex items-center gap-2 ${classes.accentText} text-sm font-semibold`}>
                  <Zap className="w-4 h-4" />
                  {step.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Choose Your Plan
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Flexible pricing for every stage of your quantum journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative ${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${
                  plan.popular ? `border-2 ${theme === 'light' ? 'border-blue-500' : theme === 'dark' ? 'border-cyan-500' : 'border-purple-500'}` : classes.cardBorder
                } animate-scale-in ${plan.popular ? 'transform lg:scale-105 shadow-2xl' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {plan.popular && (
                  <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-gradient-to-r ${classes.accent} text-white text-sm font-semibold rounded-full`}>
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-bold ${classes.text} mb-2`}>{plan.name}</h3>
                  <div className="mb-4">
                    <span className={`text-4xl font-bold ${classes.text}`}>{plan.price}</span>
                    {plan.period && <span className={classes.subtext}>{plan.period}</span>}
                  </div>
                  <p className={`${classes.subtext} text-sm`}>{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 ${classes.accentText} flex-shrink-0 mt-0.5`} />
                      <span className={`${classes.text} text-sm`}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  className={`block w-full text-center px-6 py-3 rounded-xl font-semibold transition-all ${
                    plan.popular
                      ? `bg-gradient-to-r ${classes.accent} text-white hover:shadow-lg transform hover:-translate-y-1`
                      : `border-2 ${theme === 'light' ? 'border-blue-600 text-blue-600 hover:bg-blue-50' : theme === 'dark' ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950' : 'border-purple-500 text-purple-400 hover:bg-purple-950'}`
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
            Ready to Get Started?
          </h2>
          <p className={`text-lg ${classes.subtext} mb-8`}>
            Join thousands of researchers and developers building the future with quantum computing
          </p>
          <Link
            to="/signup"
            className={`inline-flex items-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all transform hover:-translate-y-1`}
          >
            Create Free Account
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GetStarted;